// src/services/payments/payfastService.js
import CryptoJS from 'crypto-js';
import {
  getPayFastConfig,
  getPayFastITNUrls,
  PAYFAST_LIMITS,
} from '../../config/payfast';

export class PayFastService {
  // Generate payment form data
  static generatePaymentData(order, returnUrls) {
    const config = getPayFastConfig();
    const urls = getPayFastITNUrls();
    const baseUrl =
      typeof window !== 'undefined' ? window.location.origin : '';

    const sanitizeUrl = (url, fallback) => {
      if (url) {
        if (config.environment === 'live' && url.startsWith('http://')) {
          return url.replace('http://', 'https://');
        }
        return url;
      }
      return fallback;
    };

    const defaultReturn = baseUrl ? `${baseUrl}/payment/success` : '';
    const defaultCancel = baseUrl ? `${baseUrl}/payment/cancelled` : '';
    
    const paymentData = {
      // Merchant details
      merchant_id: config.merchant_id,
      merchant_key: config.merchant_key,
      
      // Order details
      return_url: sanitizeUrl(returnUrls.success, sanitizeUrl(urls.return_url, defaultReturn)),
      cancel_url: sanitizeUrl(returnUrls.cancel, sanitizeUrl(urls.cancel_url, defaultCancel)),
      notify_url: sanitizeUrl(returnUrls.notify, urls.notify_url),
      
      // Buyer details
      name_first: order.customer.firstName,
      name_last: order.customer.lastName,
      email_address: order.customer.email,
      
      // Transaction details
      m_payment_id: order.id, // Your unique payment identifier
      amount: order.total.toFixed(2),
      item_name: `Order ${order.orderNumber}`,
      item_description: this.generateItemDescription(order.items),
      
      // Optional fields
      custom_int1: order.items.length, // Number of items
      custom_str1: order.orderNumber,
      custom_str2: order.customer.phone || '',
      
      // Additional settings
      email_confirmation: 1,
      confirmation_address: order.customer.email
    };

    return paymentData;
  }

  // Generate MD5 signature for PayFast
  static generateSignature(data, passphrase = '') {
    // Create parameter string
    let paramString = '';
    const sortedKeys = Object.keys(data).sort();
    
    for (const key of sortedKeys) {
      if (key !== 'signature' && data[key] !== '' && data[key] !== null && data[key] !== undefined) {
        paramString += `${key}=${encodeURIComponent(data[key]).replace(/%20/g, '+')}&`;
      }
    }
    
    // Remove last &
    paramString = paramString.slice(0, -1);
    
    // Add passphrase if configured
    if (passphrase) {
      paramString += `&passphrase=${encodeURIComponent(passphrase)}`;
    }
    
    // Generate MD5 hash
    return CryptoJS.MD5(paramString).toString();
  }

  // Verify PayFast signature (for ITN validation)
  static verifySignature(data, receivedSignature, passphrase = '') {
    const calculatedSignature = this.generateSignature(data, passphrase);
    return calculatedSignature === receivedSignature;
  }

  // Create payment form and submit
  static redirectToPayFast(order, returnUrls = {}) {
    try {
      const paymentData = this.generatePaymentData(order, returnUrls);
    const { payment_url, environment } = getPayFastConfig();
    console.info('[PayFast] Redirecting with environment:', environment, payment_url);

      // Create form dynamically
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = payment_url;
      form.style.display = 'none';

      // Add form fields
      Object.keys(paymentData).forEach(key => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = paymentData[key];
        form.appendChild(input);
      });

      // Add form to body and submit
      document.body.appendChild(form);
      form.submit();
      
      return true;
    } catch (error) {
      console.error('Error redirecting to PayFast:', error);
      throw error;
    }
  }

  // Process PayFast return (success/cancel)
  static processReturn(urlParams) {
    const params = new URLSearchParams(urlParams);
    
    return {
      paymentId: params.get('m_payment_id'),
      paymentStatus: params.get('payment_status'),
      amount: params.get('amount_gross'),
      transactionId: params.get('pf_payment_id'),
      signature: params.get('signature')
    };
  }

  // Validate payment status
  static validatePaymentStatus(status) {
    const validStatuses = ['COMPLETE', 'FAILED', 'PENDING'];
    return validStatuses.includes(status);
  }

  // Generate item description for PayFast
  static generateItemDescription(items) {
    if (items.length === 1) {
      return `${items[0].name} (${items[0].quantity}x)`;
    } else if (items.length <= 3) {
      return items.map(item => `${item.name} (${item.quantity}x)`).join(', ');
    } else {
      return `${items.length} items including ${items[0].name}`;
    }
  }

  // Check if PayFast is available
  static isAvailable() {
    const config = getPayFastConfig();
    return !!(config.merchant_id && config.merchant_key);
  }

  // Get PayFast payment URL
  static getPaymentUrl() {
    return getPayFastConfig().payment_url;
  }

  // Validate amount (PayFast requirements)
  static validateAmount(amount) {
    const limits = PAYFAST_LIMITS;
    const numAmount = parseFloat(amount);
    
    if (isNaN(numAmount) || numAmount <= 0) {
      return { valid: false, error: 'Amount must be greater than 0' };
    }
    
    if (numAmount < limits.min_amount) {
      return { valid: false, error: `Minimum amount is R${limits.min_amount.toFixed(2)}` };
    }
    
    if (numAmount > limits.max_amount) {
      return { valid: false, error: `Maximum amount is R${limits.max_amount.toFixed(2)}` };
    }
    
    return { valid: true };
  }

  // Format amount for PayFast (2 decimal places)
  static formatAmount(amount) {
    return parseFloat(amount).toFixed(2);
  }

  // Generate test card details for sandbox
  static getTestCards() {
    return {
      successful: {
        cardNumber: '4000000000000002',
        expiryMonth: '12',
        expiryYear: '2025',
        cvv: '123',
        description: 'Successful test transaction'
      },
      insufficient_funds: {
        cardNumber: '4000000000000010',
        expiryMonth: '12',
        expiryYear: '2025',
        cvv: '123',
        description: 'Insufficient funds'
      },
      declined: {
        cardNumber: '4000000000000028',
        expiryMonth: '12',
        expiryYear: '2025',
        cvv: '123',
        description: 'Declined transaction'
      }
    };
  }

  // Handle PayFast ITN (Instant Transaction Notification)
  static async handleITN(itnData) {
    try {
      const { passphrase } = getPayFastConfig();
      // Verify signature
      const { signature, ...dataWithoutSignature } = itnData;
      if (passphrase && signature) {
        const isValidSignature = this.verifySignature(
          dataWithoutSignature,
          signature,
          passphrase
        );

        if (!isValidSignature) {
          throw new Error('Invalid PayFast signature');
        }
      }

      // Validate payment status
      const paymentStatus = itnData.payment_status;
      if (!this.validatePaymentStatus(paymentStatus)) {
        throw new Error(`Invalid payment status: ${paymentStatus}`);
      }

      // Return processed ITN data
      return {
        valid: true,
        paymentId: itnData.m_payment_id,
        transactionId: itnData.pf_payment_id,
        amount: parseFloat(itnData.amount_gross),
        fee: parseFloat(itnData.amount_fee),
        net: parseFloat(itnData.amount_net),
        status: paymentStatus,
        merchantId: itnData.merchant_id,
        itemName: itnData.item_name,
        signature: signature
      };
    } catch (error) {
      console.error('Error handling PayFast ITN:', error);
      return {
        valid: false,
        error: error.message
      };
    }
  }

  // Create payment link (alternative to form submission)
  static createPaymentLink(order, returnUrls = {}) {
    try {
      const paymentData = this.generatePaymentData(order, returnUrls);
      const { payment_url } = getPayFastConfig();

      // Create query string
      const queryString = Object.keys(paymentData)
        .map(key => `${key}=${encodeURIComponent(paymentData[key])}`)
        .join('&');

      return `${payment_url}?${queryString}`;
    } catch (error) {
      console.error('Error creating payment link:', error);
      throw error;
    }
  }

  // Get payment configuration
  static getConfig() {
    const config = getPayFastConfig();
    return {
      merchantId: config.merchant_id,
      isSandbox: config.is_sandbox,
      paymentUrl: config.payment_url
    };
  }

  // Update configuration (for different environments)
  static updateConfig(newConfig) {
    console.warn('PayFastService.updateConfig is deprecated. Configure via environment variables or config/payfast.js instead.');
    if (newConfig.environment) {
      // eslint-disable-next-line no-console
      console.info('To change environment dynamically, set REACT_APP_PAYFAST_ENV before build.');
    }
  }
}